<template>
  <!-- Формат, который браузер не декодирует (HEIC/TIFF в Chrome и Android WebView, битая
       ссылка): v-img на ошибке оставляет плейсхолдер со спиннером навсегда, поэтому
       подменяем картинку: запасной картинкой (fallbackSrc — аватары, фото авто) или иконкой
       файла с форматом. Обработчики (click/dblclick) у замены те же, что у картинки -->
  <v-img
    v-if="failed && fallbackSrc && !fallbackFailed"
    :src="fallbackSrc"
    :aspect-ratio="aspectRatio"
    v-bind="$attrs"
    class="media-thumb__fallback-image"
    v-on="$listeners"
    @error="fallbackFailed = true"
  ></v-img>
  <v-responsive
    v-else-if="failed"
    :aspect-ratio="aspectRatio"
    class="media-thumb__fallback grey lighten-2"
    :class="{ 'media-thumb__fallback--free': !aspectRatio && !compact }"
    v-on="$listeners"
  >
    <div class="media-thumb__fallback-body">
      <v-icon :size="compact ? 20 : 40" color="blue-grey">mdi-file-image-outline</v-icon>
      <span v-if="label && !compact" class="caption blue-grey--text mt-1">{{ label }}</span>
      <v-btn
        v-if="downloadHref"
        class="mt-3"
        color="primary"
        small
        :href="downloadHref"
        target="_blank"
        rel="noopener"
        @click.stop
      >
        {{ $t("analitics.download") }}
      </v-btn>
    </div>
  </v-responsive>
  <v-img
    v-else
    :src="src"
    :aspect-ratio="aspectRatio"
    class="grey lighten-2"
    v-bind="$attrs"
    v-on="$listeners"
    @error="failed = true"
  >
    <!-- С lazy-src (миниатюра провайдера в чатах) пока грузится оригинал, видна она, спиннер не нужен -->
    <template v-if="!$attrs['lazy-src']" v-slot:placeholder>
      <v-row class="fill-height ma-0" align="center" justify="center">
        <v-progress-circular
          indeterminate
          :size="compact ? 14 : 32"
          :width="compact ? 2 : 4"
          color="grey lighten-5"
        ></v-progress-circular>
      </v-row>
    </template>
  </v-img>
</template>

<script>
// "image/heic" → "HEIC": подпись под иконкой, чтобы было видно, что это за файл.
// Без типа (или с безликим application/octet-stream) — по расширению имени файла.
export function formatLabel(contentType, name) {
  const subtype = contentType ? (contentType.split("/")[1] || "") : "";
  if (subtype && subtype !== "octet-stream") return subtype.toUpperCase();
  const match = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(name || "");
  return match ? match[1].toUpperCase() : "";
}

export default {
  name: "MediaThumb",
  inheritAttrs: false,
  props: {
    src: { type: String, default: "" },
    contentType: { type: String, default: null },
    // Имя файла — для подписи формата, когда content_type неизвестен.
    name: { type: String, default: "" },
    // null — естественные пропорции (полноразмерный просмотр, вложения в чатах).
    aspectRatio: { type: [Number, String], default: 1 },
    // Мелкая клетка (аватар в списке): маленькие иконка и спиннер, без подписи.
    compact: { type: Boolean, default: false },
    // Полноразмерный просмотр: в замене кнопка «Скачать» на оригинал.
    downloadHref: { type: String, default: "" },
    // Аватары и фото авто: вместо иконки — их обычная заглушка.
    fallbackSrc: { type: String, default: "" },
  },
  data() {
    return { failed: false, fallbackFailed: false };
  },
  computed: {
    label() {
      return formatLabel(this.contentType, this.name || this.src);
    },
  },
  watch: {
    src() {
      this.failed = false;
      this.fallbackFailed = false;
    },
  },
};
</script>

<style scoped>
.media-thumb__fallback {
  cursor: pointer;
}

/* Полноразмерный просмотр и вложения: аккуратная плашка по центру, а не полоса во всю ширину */
.media-thumb__fallback--free {
  min-height: 160px;
  width: 100%;
  max-width: 480px;
  margin: auto;
  align-self: center;
  flex: 0 1 auto;
}

.media-thumb__fallback-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
